import ArchonLayout, {
  Background,
  Container,
} from '../../layouts/ArchonLayout/ArchonLayout';
import Seo from '../Seo/Seo';
import React, { useMemo, useState, type JSX } from 'react';
import { type ArchonHeaderViewModel } from '../ArchonHeader/ArchonHeader';
import { type ArchonFooterViewModel } from '../ArchonFooter/ArchonFooter';
import { type GamePageViewModel } from '../../helpers/getGamePageViewModels';
import ArchonTitle from '../ArchonTitle/ArchonTitle';
import DownloadButton from '../../../resources/js/components/DownloadButton/DownloadButton';
import Link from 'next/link';
import HorizontalImageTile, {
  type HorizontalImageTileViewModel,
} from '../HorizontalImageTile/HorizontalImageTile';
import styles from './GamePage.module.scss';
import VerticalContent from '../../../resources/js/components/VerticalContent/VerticalContent';
import EnhancedMarkdown from '../../../resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import TinyArticlePreview from '@resources/js/components/TinyArticlePreview/TinyArticlePreview';
import convertImageUrlToOptimizedImageUrl from '../../helpers/convertImageUrlToOptimizedImageUrl';
import ArchonLogo from '../ArchonLogo/ArchonLogo';
import ActorIcon from '@resources/js/components/ActorIcon/ActorIcon';
import { type GameViewModel } from '@resources/js/typings/viewModels/gameViewModel';
import { GameContextProvider } from '@resources/js/contexts/useGame';
import LinkWrapper from '../LinkWrapper/LinkWrapper';
import BuildsTierListSection from '@resources/js/components/BuildsTierListSection/BuildsTierListSection';
import BuildsZonePageFilter from '../ZonePageFilter/BuildsZonePageFilter';
import Heading from '@resources/js/components/Heading/Heading';
import Body from '@resources/js/components/Body/Body';
import useGameSlug from '../../hooks/useGameSlug';
import { type BuildsTierList } from '../../helpers/getBuildsZonePageViewModels';
import cx from 'classnames';
import Button from '@resources/js/components/Button/Button';

export type GamePageProps = {
  game: GameViewModel;
  header: ArchonHeaderViewModel;
  footer: ArchonFooterViewModel;
  page: GamePageViewModel;
};

export default function GamePage({
  game,
  header,
  footer,
  page,
}: GamePageProps): JSX.Element {
  const {
    seo,
    breadcrumbs,
    heading,
    body,
    downloadUrl,
    downloadButtonLabel,
    downloadButtonDescription,
    latestNewsLabel,
    resources,
    builds,
    tierList,
    articlePreview,
    classesAndSpecsUrl,
  } = page;
  const gameSlug = useGameSlug();

  const groupedResources = resources.reduce<
    Record<string, Array<HorizontalImageTileViewModel>>
  >((acc, resource) => {
    (acc[resource.categoryTitle] = acc[resource.categoryTitle] || []).push(
      resource,
    );
    return acc;
  }, {});

  const tierLists: BuildsTierList[] = useMemo(() => {
    if (!tierList) {
      return [];
    }

    const metric =
      tierList.buildsZonePageViewModels[0].specTierListSection.tierLists[0]
        .metric;
    const tiers =
      tierList.buildsZonePageViewModels[0].specTierListSection.tierLists[0]
        .tiers;

    return [
      {
        metric,
        tiers: tiers.map((tier) => ({
          tier: tier.tier,
          entries: tierList.buildsZonePageViewModels.map(
            (zonePage) =>
              zonePage.specTierListSection.tierLists
                .find((tierList) => tierList.metric === metric)
                ?.tiers.find((x) => x.tier === tier.tier)
                ?.entries.flat() ?? [],
          ),
        })),
      },
    ];
  }, [tierList]);

  const latestArticle = articlePreview
    ? {
        title: articlePreview.title,
        url: articlePreview.url,
        timestamp: articlePreview.timestamp,
        thumbnailUrl: articlePreview.thumbnailUrl,
      }
    : footer.latestArticle;

  const [selectedClass, setSelectedClass] = useState('');
  const titleBadgeLabel = game.lowercaseName === 'ff' ? 'Beta' : undefined;

  return (
    <GameContextProvider game={game}>
      <ArchonLayout header={header} breadcrumbs={breadcrumbs} footer={footer}>
        <Seo {...seo} />
        <Background />
        <Container className={styles.topSectionContainer}>
          <ArchonTitle heading={heading} badgeLabel={titleBadgeLabel} />
          <VerticalContent gap={'tiny'}>
            {builds && (
              <div className={styles.buildsSectionContainer}>
                <div className={styles.buildsPickSpecContainer}>
                  <Link href={classesAndSpecsUrl}>
                    <h2 className={styles.buildsHeading}>
                      <span className={styles.buildsTitlePartOne}>
                        {builds.titlePartOne}
                      </span>
                      <span className={styles.buildsTitlePartTwo}>
                        {builds.titlePartTwo}
                      </span>
                    </h2>
                  </Link>
                  <Body>
                    <p>{builds.description}</p>
                  </Body>
                </div>
                {tierList && (
                  <div className={styles.buildsClassesContainer}>
                    <span className={styles.buildsPickSpecDescription}>
                      {builds.pickSpecText}
                    </span>
                    <div className={styles.buildsIconsContainer}>
                      {builds.classesAndSpecs.map((classAndSpecs) => (
                        <Button
                          styles={['unstyled']}
                          key={classAndSpecs.className}
                          className={styles.buildsSpecContainer}
                          onClick={() =>
                            setSelectedClass(
                              classAndSpecs.classSlug !== selectedClass
                                ? classAndSpecs.classSlug
                                : '',
                            )
                          }
                        >
                          <div
                            className={`${styles.buildsSpecIcon} ${
                              selectedClass === classAndSpecs.classSlug
                                ? styles.buildsSpecIconSelected
                                : ''
                            }`}
                          >
                            <ActorIcon
                              type={
                                classAndSpecs.isGroupedByClass
                                  ? classAndSpecs.cssClassName
                                  : classAndSpecs.roleType
                              }
                              useSecondarySprites={
                                classAndSpecs.useSecondarySprites
                              }
                              size={44}
                              rounded={false}
                              bordered={classAndSpecs.isGroupedByClass}
                            />
                          </div>
                          <span>
                            {classAndSpecs.className.indexOf(' ') > -1
                              ? classAndSpecs.shortClassName
                              : classAndSpecs.className}
                          </span>
                        </Button>
                      ))}
                    </div>
                    {selectedClass !== '' && (
                      <div
                        className={`${styles.buildsIconsContainer} ${styles.buildsSpecIconsContainer}`}
                      >
                        {builds.classesAndSpecs
                          .find(
                            (classAndSpecs) =>
                              classAndSpecs.classSlug === selectedClass,
                          )
                          ?.specs.map((spec) => (
                            <div
                              key={spec.name}
                              className={styles.buildsSpecContainer}
                            >
                              <LinkWrapper href={spec.buildsPath}>
                                <div>
                                  <ActorIcon
                                    type={spec.fullType}
                                    size={44}
                                    useSecondarySprites={
                                      spec.useSecondarySprites
                                    }
                                    rounded={false}
                                    bordered={false}
                                  />
                                </div>
                                <span>{spec.shortSpecName}</span>
                              </LinkWrapper>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {tierList ? (
              <div className={styles.tierListSectionContainer}>
                <div className={styles.tierListDescriptionContainer}>
                  <Link href={tierList.tierListUrl}>
                    <h2 className={styles.buildsHeading}>
                      <span
                        className={cx([
                          styles.buildsTitlePartOne,
                          styles.buildsTitlePartOneWhite,
                        ])}
                      >
                        {tierList.titlePartOne} {tierList.titlePartTwo}
                      </span>
                    </h2>
                  </Link>
                  <Body>
                    <p>{tierList.description}</p>
                  </Body>
                </div>
                <div className={styles.tierListContainer}>
                  <Body>
                    <BuildsZonePageFilter
                      {...tierList.buildsZonePageViewModels[0]}
                      selectedRole={null}
                      encounterOptions={[]}
                      affixesOptions={[]}
                    />
                  </Body>
                  <Body>
                    <BuildsTierListSection
                      LinkWrapper={LinkWrapper}
                      {...tierList.buildsZonePageViewModels[0]
                        .specTierListSection}
                      tierLists={tierLists}
                      showTierListDescription={false}
                      showMetricOptionsAndDescription={false}
                      useCompactEntries={true}
                      gridTemplateColumns={tierList.gridTemplateColumns}
                      showTierListGroupLabel={false}
                    />
                  </Body>
                </div>
              </div>
            ) : (
              <div className={styles.specLinksContainer}>
                {builds?.classesAndSpecs?.map((classAndSpecs) => (
                  <div
                    key={classAndSpecs.classSlug}
                    className={styles.specLinksColumn}
                  >
                    <div className={styles.specLinksColumnLabel}>
                      <ActorIcon
                        type={
                          classAndSpecs.isGroupedByClass
                            ? classAndSpecs.cssClassName
                            : classAndSpecs.roleType
                        }
                        useSecondarySprites={classAndSpecs.useSecondarySprites}
                        size={32}
                        rounded={false}
                        bordered={classAndSpecs.isGroupedByClass}
                      >
                        {classAndSpecs.className}
                      </ActorIcon>
                    </div>
                    <div className={styles.specLinksColumnContents}>
                      {classAndSpecs.specs.map((spec) => (
                        <LinkWrapper href={spec.buildsPath} key={spec.fullType}>
                          <div className={styles.specLinksColumnLink}>
                            <div
                              className={`${styles.specLinksSpecIcon} ${spec.fullType}-border-color ${spec.fullType}-gradient-bg`}
                            >
                              <ActorIcon
                                type={spec.fullType}
                                size={44}
                                useSecondarySprites={spec.useSecondarySprites}
                                bordered={false}
                              ></ActorIcon>
                            </div>
                            <div className={spec.fullType}>
                              {spec.shortSpecName}
                            </div>
                          </div>
                        </LinkWrapper>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </VerticalContent>
        </Container>
        <Container className={styles.accentContainer}>
          <VerticalContent
            className={styles.accentContainerVerticalPadding}
            gap={'xlarge'}
          >
            <EnhancedMarkdown markdown={body} />
            <div className={styles.newsAndDownloadBannerContainer}>
              <div className={styles.stayUpToDateSectionContainer}>
                <span className={styles.stayUpToDateText}>
                  <Link href={`/${gameSlug}/articles`}>{latestNewsLabel}</Link>
                </span>
                <Link href={latestArticle.url}>
                  <TinyArticlePreview
                    title={latestArticle.title}
                    timestamp={latestArticle.timestamp as number}
                    thumbnailUrl={convertImageUrlToOptimizedImageUrl(
                      latestArticle.thumbnailUrl,
                      128,
                      75,
                    )}
                  />
                </Link>
              </div>
              <div className={styles.archonLogoContainer}>
                <ArchonLogo size={71} />
              </div>
              <div className={styles.startLoggingContainer}>
                <div className={styles.stayUpToDateText}>
                  <span>
                    <Link href={downloadUrl}>{downloadButtonDescription}</Link>
                  </span>
                </div>
                <div className={styles.downloadSection}>
                  <DownloadButton href={downloadUrl}>
                    {downloadButtonLabel}
                  </DownloadButton>
                </div>
              </div>
            </div>
          </VerticalContent>
        </Container>
        <Container>
          <div className={styles.resourceGroupsContainer}>
            {Object.values(groupedResources).map((resourceGroup) => (
              <div key={resourceGroup[0].categoryTitle}>
                <Heading level={2}>{resourceGroup[0].categoryTitle}</Heading>
                <div className={styles.resourceGroupContainer}>
                  {resourceGroup.map((resource) => (
                    <div key={resource.content} className={styles.resource}>
                      <HorizontalImageTile
                        imageUrl={resource.imageUrl}
                        titlePartOne={resource.titlePartOne}
                        titlePartTwo={resource.titlePartTwo}
                        content={resource.content}
                        categoryTitle={resource.categoryTitle}
                        containerClassName={styles.resourceImage}
                        href={resource.href}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </ArchonLayout>
    </GameContextProvider>
  );
}
